// Shared directory data. Provider listings live here until verified listings are published.
export const LOCATIONS = [
  "George Town",
  "Tanjung Tokong",
  "Bayan Lepas",
  "Air Itam",
  "Butterworth",
  "Bukit Mertajam",
  "Seberang Perai",
];

export const CARE_TYPES = [
  { value: "general", label: "General elderly care" },
  { value: "residential-nursing", label: "Residential nursing care" },
  { value: "dementia", label: "Dementia care" },
  { value: "rehabilitation", label: "Rehabilitation or recovery care" },
  { value: "respite", label: "Respite care" },
  { value: "daycare", label: "Daycare" },
];

export const DURATIONS = [
  { value: "daily", label: "Daily / Daycare", href: "/services/daily-daycare/" },
  { value: "weekly", label: "Weekly / Short-Term Respite", href: "/services/weekly-short-stay/" },
  { value: "monthly", label: "Monthly / Long-Term Residential", href: "/services/monthly-long-term/" },
];

// Verified provider listings. Never invent facilities — only publish verified data.
// Shape: { id, name, area, address, description, careTypes: [], durations: [],
//          roomInfo, priceInfo, phone, website, image, lastVerified }
export const PROVIDERS = [];

export const CATEGORIES = [
  {
    title: "Nursing Homes in Penang",
    description: "Residential homes providing accommodation, meals and daily living support for seniors.",
    href: "/categories/assisted-living/",
    icon: "Building2",
    color: "teal",
  },
  {
    title: "Elderly Care Homes in Penang",
    description: "Long-term residential options for older adults who need ongoing daily care.",
    href: "/services/monthly-long-term/",
    icon: "Home",
    color: "gold",
  },
  {
    title: "Dementia Care in Penang",
    description: "Providers families can approach about dementia and Alzheimer's support.",
    href: "/categories/dementia-and-rehab/",
    icon: "Brain",
    color: "coral",
  },
  {
    title: "Rehabilitation & Recovery Care",
    description: "Post-hospitalisation recovery and rehabilitation support for seniors.",
    href: "/categories/dementia-and-rehab/",
    icon: "Activity",
    color: "sage",
  },
  {
    title: "Respite Care for Seniors",
    description: "Short stays that give family caregivers a planned, supported break.",
    href: "/services/weekly-short-stay/",
    icon: "CalendarClock",
    color: "teal",
  },
  {
    title: "Long-Term Residential Care",
    description: "Monthly residential care arrangements across Penang island and mainland.",
    href: "/services/monthly-long-term/",
    icon: "BedDouble",
    color: "gold",
  },
];

export const SEVEN_STEPS = [
  {
    n: 1,
    color: "#075B59",
    title: "Contact Us via WhatsApp",
    short: "Message us on WhatsApp to explain your needs and request help finding a suitable nursing home in Penang.",
    points: [
      "Families contact us through WhatsApp to explain their needs.",
      "We start exploring potentially suitable options right away.",
    ],
  },
  {
    n: 2,
    color: "#C99A52",
    title: "Share Your Requirements and Budget",
    short: "Tell us the preferred location, care requirements, room type, duration, monthly budget and admission date.",
    points: [
      "Preferred location and expected admission date",
      "Resident's age, mobility and daily assistance needs",
      "Dementia care or other specialised requirements, if applicable",
      "Preferred room type, daily/weekly/monthly duration and monthly budget",
      "We collect only what is needed to identify suitable options — never sensitive medical records through unsecured channels.",
    ],
  },
  {
    n: 3,
    color: "#E96D85",
    title: "Arrange Nursing Home Viewings",
    short: "We help coordinate viewing appointments with facilities that may match your requirements.",
    points: [
      "Inspect the environment and ask about staffing and care arrangements.",
      "Review fees and assess suitability in person.",
      "A viewing does not guarantee acceptance or admission.",
    ],
  },
  {
    n: 4,
    color: "#83A96A",
    title: "Pre-Registration",
    short: "We help coordinate the selected facility's pre-registration process.",
    points: [
      "Completing forms and preparing identification documents",
      "Providing relevant care information",
      "Reviewing deposits, fees and payment arrangements",
      "All approval decisions remain subject to the facility's policies.",
    ],
  },
  {
    n: 5,
    color: "#075B59",
    title: "Arrange Resident Logistics and Personal Items",
    short: "We help coordinate practical arrangements before move-in.",
    points: [
      "Transport to the nursing home",
      "Clothing, toiletries, personal belongings and daily necessities",
      "Mobility aids, current medication information and relevant documents",
      "Emergency contact details",
      "Transport and additional logistical services may incur separate charges.",
    ],
  },
  {
    n: 6,
    color: "#C99A52",
    title: "Resident Admission",
    short: "On the agreed date, the family and facility complete the admission procedures.",
    points: [
      "The facility confirms acceptance and receives the required documents and belongings.",
      "House rules, applicable fees and payment arrangements are confirmed.",
      "Admission remains subject to the facility's approval.",
    ],
  },
  {
    n: 7,
    color: "#83A96A",
    title: "Create a WhatsApp Group for Family Updates",
    short: "Where agreed, we help set up a WhatsApp group between the family and the facility contact.",
    points: [
      "General updates from the facility and family enquiries",
      "Reminders about personal items and administrative arrangements",
      "Clinical assessments, medical advice and care decisions always come from the responsible facility staff or healthcare professionals.",
      "Consent is obtained before adding members, and the resident's privacy is respected.",
    ],
  },
  {
    n: 8,
    color: "#E96D85",
    title: "Follow-Up Support After Move-In",
    short: "We check in with the family after move-in and stay available to assist with ongoing coordination.",
    points: [
      "We follow up with the family after the resident has moved in to check whether the initial arrangements have gone smoothly.",
      "We confirm whether any further coordination is needed.",
      "We remain available to assist with communication between the family and the nursing home, where applicable.",
    ],
  },
];

// These eight questions and answers appear verbatim on the homepage and in its FAQPage JSON-LD.
export const FAQS = [
  {
    q: "How much does a nursing home cost in Penang?",
    a: "Nursing home fees in Penang vary according to room type, level of care, nursing requirements, location and additional services. Ask each facility for its current fees, deposit, inclusions and any extra charges before making a decision.",
  },
  {
    q: "How do I choose the right nursing home for my parents?",
    a: "Consider your parent's care needs, staff availability, cleanliness, safety, food, room arrangements, visiting policies and overall costs. Visit the facility, ask questions and verify its relevant registration or licensing status before admission.",
  },
  {
    q: "What is the difference between a nursing home and an elderly care home?",
    a: "The terms can overlap. Some facilities provide residential accommodation and assistance with daily activities, while others also provide nursing care or support for more complex needs. Confirm the actual services and staffing arrangements directly with each provider.",
  },
  {
    q: "Are there dementia care homes in Penang?",
    a: "Families can explore elderly care providers in Penang and ask whether they accept residents with dementia or Alzheimer's disease. Confirm staff training, supervision, safety measures, activities and the facility's ability to meet the person's individual needs.",
  },
  {
    q: "Can I arrange daily daycare or short-term respite care for an elderly family member?",
    a: "Some providers may offer daytime care or short-term respite stays, but services and availability vary. Contact each facility to confirm its operating hours, admission requirements, fees and suitability for your family member.",
  },
  {
    q: "What should I prepare before admitting my parent to a nursing home?",
    a: "Ask the facility for its admission checklist. Depending on its requirements, you may need identification documents, current medication details, relevant medical information, emergency contacts, clothing and personal necessities.",
  },
  {
    q: "Can I use EPF savings to pay for nursing home care?",
    a: "EPF withdrawals are subject to the eligibility conditions and permitted purposes under current KWSP rules. Do not assume that ordinary nursing home fees qualify. Check the latest official KWSP guidance or contact KWSP before making financial commitments.",
  },
  {
    q: "How can I find and contact a nursing home in Penang?",
    a: "Browse the directory by location and type of care, review the available provider information, and contact facilities directly to confirm their services, fees and vacancies. You can also submit an enquiry through nursinghomeinpenang.com to seek help exploring suitable options.",
  },
];
